import { DOCUMENT_TYPE } from '../../../document/constants.ts';
import { CategoryFormData, CATEGORY_FORM_DEFAULT_VALUES } from './CreateCustomCategoryModal.tsx';
import {
    CustomCategoryActionInput,
    CustomCategoryActionKind,
    ForwardOption,
    SmartInboxCustomCategoryType,
} from './types.ts';

export const CHANNEL_KEY_TO_DOC_TYPE: Record<string, string> = {
    rfqProcessingChannels: DOCUMENT_TYPE.REQUEST_FOR_QUOTATION,
    orderProcessingChannels: DOCUMENT_TYPE.ORDER,
    orderConfirmationProcessingChannels: DOCUMENT_TYPE.ORDER_CONFIRMATION,
    invoiceProcessingChannels: DOCUMENT_TYPE.INVOICE,
    propertyBillProcessingChannels: DOCUMENT_TYPE.PROPERTY_BILL,
    deliveryNoteProcessingChannels: DOCUMENT_TYPE.DELIVERY_NOTE,
    listOfServicesProcessingChannels: DOCUMENT_TYPE.LIST_OF_SERVICES,
    universalProcessingChannels: DOCUMENT_TYPE.UNIVERSAL,
};

export const getChannelDocType = (channelData: Record<string, any> | undefined, channelId: string): string | null => {
    if (!channelData) return null;
    for (const [key, val] of Object.entries(channelData)) {
        if (key === 'agents' || key === '__typename' || !Array.isArray(val)) continue;
        if ((val as any[]).some((c: any) => c?.id === channelId)) {
            return CHANNEL_KEY_TO_DOC_TYPE[key] ?? null;
        }
    }
    return null;
};

// Form data -> mutation `actions` input, for CREATE_CUSTOM_CATEGORY / UPDATE_CUSTOM_CATEGORY.
export const buildCategoryActions = (
    formData: CategoryFormData,
    channelData: Record<string, any> | undefined
): CustomCategoryActionInput[] => {
    const actions: CustomCategoryActionInput[] = [];
    if (formData.outlookTag?.length > 0) {
        actions.push({
            actionKind: CustomCategoryActionKind.TAG,
            actionConfig: JSON.stringify({ name: formData.outlookTag[0], color: formData.tagColor }),
        });
    }
    actions.push({
        actionKind: CustomCategoryActionKind.MOVE_TO_FOLDER,
        actionConfig: JSON.stringify({ folder_id: formData.moveToOutlookFolder || '' }),
    });
    actions.push({
        actionKind: CustomCategoryActionKind.MARK_AS_READ,
        actionConfig: JSON.stringify({ mark_as_read: formData.markAsRead ?? false }),
    });

    let createRecordConfig = {};
    if (
        formData.enableAutoforwarding &&
        formData.forwardOption === ForwardOption.WORKIST &&
        formData.forwardingChannel
    ) {
        const docType = getChannelDocType(channelData, formData.forwardingChannel);
        if (docType) createRecordConfig = { [docType]: formData.forwardingChannel };
    }
    actions.push({
        actionKind: CustomCategoryActionKind.CREATE_RECORD,
        actionConfig: JSON.stringify(createRecordConfig),
    });

    const forwardEmails =
        formData.enableAutoforwarding && formData.forwardOption === ForwardOption.EMAIL
            ? (formData.forwardingEmailAddresses ?? [])
            : [];
    actions.push({
        actionKind: CustomCategoryActionKind.FORWARD,
        actionConfig: JSON.stringify({ emails: forwardEmails }),
    });

    return actions;
};

// Existing category (from GET_SMART_INBOX_INTEGRATION) -> form data, for editing.
export const categoryToFormData = (category: SmartInboxCustomCategoryType): CategoryFormData => {
    const tagAction = category.actions?.find((a) => a.actionKind === CustomCategoryActionKind.TAG);
    const tagConfig = tagAction
        ? (() => {
              const parsed = JSON.parse(tagAction.actionConfig ?? '{}');
              return parsed?.action_config ?? parsed;
          })()
        : null;

    const moveAction = category.actions?.find((a) => a.actionKind === CustomCategoryActionKind.MOVE_TO_FOLDER);
    const moveConfig = moveAction ? JSON.parse(moveAction.actionConfig ?? '{}') : null;

    const markAsReadAction = category.actions?.find((a) => a.actionKind === CustomCategoryActionKind.MARK_AS_READ);
    const markAsReadConfig = markAsReadAction ? JSON.parse(markAsReadAction.actionConfig ?? '{}') : null;

    const createRecordAction = category.actions?.find((a) => a.actionKind === CustomCategoryActionKind.CREATE_RECORD);
    const createRecordConfig = createRecordAction ? JSON.parse(createRecordAction.actionConfig ?? '{}') : null;
    const forwardingChannelId =
        createRecordConfig && Object.keys(createRecordConfig).length > 0
            ? (Object.values(createRecordConfig)[0] as string)
            : '';

    const forwardAction = category.actions?.find((a) => a.actionKind === CustomCategoryActionKind.FORWARD);
    const forwardConfig = forwardAction ? JSON.parse(forwardAction.actionConfig ?? '{}') : null;
    const forwardingEmails: string[] = forwardConfig?.emails ?? [];

    const hasChannel = !!forwardingChannelId;
    const hasEmails = forwardingEmails.length > 0;
    const enableAutoforwarding = hasChannel || hasEmails;
    const forwardOption = hasChannel ? ForwardOption.WORKIST : hasEmails ? ForwardOption.EMAIL : ForwardOption.WORKIST;

    return {
        name: category.name || '',
        detectionCriteria: category.detectionCriteria || '',
        supportingKeywords: category.supportingKeywords || [],
        outlookTag: tagConfig?.name ? [tagConfig.name] : [],
        tagColor: tagConfig?.color || CATEGORY_FORM_DEFAULT_VALUES.tagColor,
        moveToOutlookFolder: moveConfig?.folder_id || '',
        markAsRead: markAsReadConfig?.mark_as_read ?? false,
        enableAutoforwarding,
        forwardOption,
        forwardingChannel: forwardingChannelId,
        forwardingEmailAddresses: forwardingEmails,
    };
};

// Used to darken outlook tag hex as text colors
export const darkenHex = (hex: string, amount = 130): string => {
    const num = parseInt(hex.slice(1), 16);
    const r = Math.max(0, (num >> 16) - amount);
    const g = Math.max(0, ((num >> 8) & 0xff) - amount);
    const b = Math.max(0, (num & 0xff) - amount);
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
};

export const getCategoryOutlookTag = (
    category: SmartInboxCustomCategoryType
): { name: string; colorName?: string } | null => {
    const tagAction = category.actions?.find((a) => a.actionKind === CustomCategoryActionKind.TAG);
    if (!tagAction) return null;
    const parsed = JSON.parse(tagAction.actionConfig ?? '{}');
    const config = parsed?.action_config ?? parsed;
    return config?.name ? { name: config.name, colorName: config.color } : null;
};

export const categoryHasWorkbenchAutomation = (category: SmartInboxCustomCategoryType): boolean => {
    const createRecordAction = category.actions?.find((a) => a.actionKind === CustomCategoryActionKind.CREATE_RECORD);
    if (!createRecordAction) return false;
    const config = JSON.parse(createRecordAction.actionConfig ?? '{}');
    return Object.keys(config).length > 0;
};
